import { useState } from "react";

import { api, ApiError } from "../lib/api";

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  prefix?: string;            // 上传 COS 路径前缀，默认 gallery/
  accept?: string;            // file input accept，默认 image/*
};

const fileUrl = (key: string) =>
  /^https?:\/\//.test(key) || key.startsWith("/") ? key : `/api/files/${key}`;

/** 简易画廊编辑器：缩略图 + 左移/右移/删除 + 添加（多选上传） */
export function GalleryEditor({ value, onChange, prefix = "gallery/", accept = "image/*" }: Props) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = value || [];

  const move = (idx: number, dir: -1 | 1) => {
    const next = items.slice();
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const remove = (idx: number) => {
    const removed = items[idx];
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
    api.dropFile(removed); // 删图：清对象存储文件（全库复查，仍被引用则保留）
  };
  const addUrl = () => {
    const url = prompt("粘贴图片 URL（或 COS Key）：");
    if (!url) return;
    onChange([...(items || []), url.trim()]);
  };
  const upload = async (files: FileList) => {
    setError(null);
    if (!prefix) { setError("请先填写项目 slug，再上传图片"); return; }
    setUploading(true);
    const added: string[] = [];
    try {
      for (const f of Array.from(files)) {
        try {
          const res = await api.upload<{ key: string; url: string }>(
            "/api/admin/uploads",
            f,
            prefix
          );
          added.push(res.url);
        } catch (err) {
          if (err instanceof ApiError && err.status === 503) {
            setError("COS 凭证未配置（apps/api/.env 需填 COS_SECRET_ID 等）");
            break;
          }
          if (err instanceof ApiError) {
            setError(`上传失败：${JSON.stringify(err.body)}`);
            break;
          }
          setError(String(err));
          break;
        }
      }
      if (added.length > 0) onChange([...(items || []), ...added]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="gallery-editor">
      <div className="gallery-editor-grid">
        {items.map((src, idx) => (
          <div className="gallery-item" key={`${src}-${idx}`}>
            <div className="gallery-thumb">
              <img src={fileUrl(src)} alt="" onError={(e) => (e.currentTarget.style.opacity = "0.2")} />
            </div>
            <div className="gallery-actions">
              <button type="button" className="btn btn-xs" onClick={() => move(idx, -1)} disabled={idx === 0} title="左移">←</button>
              <button type="button" className="btn btn-xs" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} title="右移">→</button>
              <button type="button" className="btn btn-xs btn-danger" onClick={() => remove(idx)} title="删除">×</button>
            </div>
            <div className="gallery-meta" title={src}>{src.split("/").pop()}</div>
          </div>
        ))}

        <label className="gallery-add">
          <input
            type="file"
            accept={accept}
            multiple
            style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files; if (f && f.length > 0) upload(f); e.target.value = ""; }}
            disabled={uploading}
          />
          <div className="gallery-add-inner">
            <div style={{ fontSize: 22 }}>+</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>{uploading ? "上传中…" : "添加图片"}</div>
          </div>
        </label>
      </div>

      <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" className="btn btn-xs" onClick={addUrl}>+ 粘贴 URL</button>
        {error && <span className="error" style={{ fontSize: 12 }}>{error}</span>}
        {!error && <span className="muted" style={{ fontSize: 12 }}>{items.length} 张</span>}
      </div>
    </div>
  );
}
